import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { SHIFT_CATEGORIES, SURGERY_CATEGORIES } from "../../common/residentCategories";

// Log a dated shift or surgery for a resident.
const LogDialog = ({ open, residents, initialResident, initialKind, onClose, onSave, saving }) => {
  const [resident, setResident] = useState("");
  const [kind, setKind] = useState("shift");
  const [category, setCategory] = useState("");
  const [date, setDate] = useState(dayjs());
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setResident(initialResident || "");
      setKind(initialKind || "shift");
      setCategory("");
      setDate(dayjs());
      setNotes("");
      setError("");
    }
  }, [open, initialResident, initialKind]);

  const categories = kind === "shift" ? SHIFT_CATEGORIES : SURGERY_CATEGORIES;

  const handleKindChange = (event, value) => {
    if (value) {
      setKind(value);
      setCategory("");
    }
  };

  const handleSave = () => {
    if (!resident || !category || !date || !date.isValid()) {
      setError("Resident, type and a valid date are required.");
      return;
    }
    onSave({ resident, kind, category, date: date.format("YYYY-MM-DD"), notes: notes.trim() })
      .then(onClose)
      .catch((err) => setError(err?.data?.message || "Failed to save entry."));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{kind === "shift" ? "Log shift" : "Log surgery"}</DialogTitle>
      <DialogContent>
        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          value={kind}
          onChange={handleKindChange}
          sx={{ mt: "0.5rem", mb: "1rem" }}
        >
          <ToggleButton value="shift">Εφημερία</ToggleButton>
          <ToggleButton value="surgery">Χειρουργείο</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          select
          fullWidth
          label="Ειδικευόμενος"
          value={resident}
          onChange={(e) => setResident(e.target.value)}
          sx={{ mb: "1rem" }}
        >
          {(residents || []).map((r) => (
            <MenuItem key={r._id} value={r._id}>
              {r.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          fullWidth
          label={kind === "shift" ? "Rotation" : "Surgery type"}
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          sx={{ mb: "1rem" }}
        >
          {categories.map(({ key, label }) => (
            <MenuItem key={key} value={key}>
              {label}
            </MenuItem>
          ))}
        </TextField>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Date"
            format="DD/MM/YYYY"
            value={date}
            onChange={setDate}
            disableFuture
            slotProps={{ textField: { fullWidth: true, sx: { mb: "1rem" } } }}
          />
        </LocalizationProvider>
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        {error && (
          <Typography color="error" sx={{ mt: "0.75rem" }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="secondary" onClick={handleSave} disabled={saving}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LogDialog;
